/**
 * Frame tags for planet names — GS., GT., HT., HS.
 *
 * A label reading "Mars in Aries" does not say which Mars. Geocentric sidereal, geocentric
 * tropical, heliocentric tropical and heliocentric sidereal put Mars in different signs on the
 * same day, so two overlays on one chart could carry identical captions and mean different things.
 * The tag names both halves of the frame: first letter the centre, second the zodiac.
 *
 * One definition, used by the Area Highlight dialog, the MOA condition list and anything that
 * renders either — the chart passes labels through untouched, so tagging at the source reaches
 * the legend and tooltips without those knowing about frames at all.
 */

/** Wire frame value → tag. The keys are what the dialogs and the backend already exchange. */
export const FRAME_PREFIX = {
  'geo-sidereal': 'GS',
  'geo-tropical': 'GT',
  'helio': 'HT',
  'helio-sidereal': 'HS',
  // The pre-rename spellings, still sent by older saved studies.
  'sidereal': 'GS',
  'tropical': 'GT',
  'heliocentric': 'HT',
};

/** Long names, for a legend key or a tooltip that has room to spell it out. */
export const FRAME_NAME = {
  GS: 'Geocentric Sidereal',
  GT: 'Geocentric Tropical',
  HT: 'Heliocentric Tropical',
  HS: 'Heliocentric Sidereal',
};

/**
 * The tag for a frame, or null when there is no frame to report.
 *
 * Returns null rather than guessing a default. A wrong tag is worse than no tag: it states
 * something specific and false about which coordinates produced the band.
 */
export function framePrefix(frame) {
  if (!frame) return null;
  return FRAME_PREFIX[String(frame).trim().toLowerCase()] || null;
}

/**
 * Tags a planet name: `withFrame('Mars', 'helio')` → `'HT.Mars'`.
 *
 * An unknown frame leaves the name alone, so a study type that has no frame keeps reading the way
 * it always did instead of gaining a misleading prefix.
 */
export function withFrame(planet, frame) {
  const tag = framePrefix(frame);
  if (!tag || !planet) return planet;
  return `${tag}.${planet}`;
}

/**
 * The frame an MOA condition was computed in, derived from its type.
 *
 * Each criteria tab fixes a frame, so the condition's own type is what determines the tag — the
 * user never picks one except on the two heliocentric sign rows, which carry it explicitly.
 *
 * Returns null for conditions with no planet in them (a weekday, a tithi, a yog) so nothing is
 * tagged that has no position to be tagged with.
 */
export function frameOfCondition(cond) {
  if (!cond || !cond.type) return null;
  const type = cond.type;

  // The two rows that ask. Their radio sends "tropical" or "sidereal", meaning the zodiac only —
  // the centre is already heliocentric by virtue of the row.
  if (type === 'helio_planet_in_sign' || type === 'helio_two_planets_in_sign') {
    const zodiac = String(cond.zodiac || cond.frame || 'tropical').toLowerCase();
    return zodiac === 'sidereal' ? 'HS' : 'HT';
  }
  // Separation is counted in sidereal divisions; see the backend's helio_planets_apart.
  if (type === 'helio_planets_apart') return 'HS';

  // Geocentric, with the zodiac carried on the condition because this row appears in both
  // sections of Zodiac-Geo. Falling through to the default would tag the tropical row GS — and
  // the tag exists precisely so two otherwise identical labels can be told apart.
  if (type === 'geo_conjunction_phase') {
    return String(cond.frame || 'tropical').toLowerCase() === 'sidereal' ? 'GS' : 'GT';
  }
  // The longitude-based heliocentric rows are tropical, which their labels now say aloud.
  if (type.startsWith('helio_')) return 'HT';

  if (type.startsWith('tropical_') || type === 'harmonic') return 'GT';

  // Everything else on the dialog is geocentric sidereal: Groups 1, 3, 4, 5, 6 and the natal rows.
  if (NO_PLANET_TYPES.has(type)) return null;
  return 'GS';
}

/**
 * Condition types with no planet in them.
 *
 * A calendar day, a weekday, a lunar month and a yog are moments in time rather than positions of
 * a body, so there is nothing for a frame tag to qualify.
 */
const NO_PLANET_TYPES = new Set([
  'g5_day_is', 'g5_month_is', 'g5_paksha_tithi', 'g5_weekday', 'g5_yog',
  'g5_weekday_yog', 'g5_vedic_month',
  'g3_rahu_ketu_between', 'g3_ketu_rahu_between',
  'g4_rahu_ketu_between', 'g4_ketu_rahu_between',
]);

/** Tags a planet with the frame its condition was computed in. */
export function planetInCondition(planet, cond) {
  const tag = frameOfCondition(cond);
  if (!tag || !planet) return planet;
  return `${tag}.${planet}`;
}
